import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  User,
  Tag,
  ChevronRight,
  Share2,
  Twitter,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import BlogPortableText from '../components/PortableText';
import { CARD_CODE_REGEX } from '../utils/cardCodes';
import { fetchPostBySlug, urlFor } from '../lib/sanity';
import { useLanguage } from '../contexts/LanguageContext';
import { getProxyImageUrl } from '../utils';

/**
 * BlogPost – individual article page with full Portable Text rendering,
 * auto-detected interactive card codes, and a Related Cards sidebar.
 */
export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  useLanguage();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedCards, setRelatedCards] = useState([]);
  const [expectedCardCount, setExpectedCardCount] = useState(0);

  // ── Fetch post data ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPostBySlug(slug).then((data) => {
      if (!cancelled) {
        setPost(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ── Detect card codes in content + featuredCards ──────────
  useEffect(() => {
    if (!post) return;

    const codes = new Set(post.featuredCards || []);

    // Also scan body text for card codes
    const regex = new RegExp(CARD_CODE_REGEX.source, 'g');
    post.content?.forEach((block) => {
      block.children?.forEach((child) => {
        if (typeof child.text === 'string') {
          let m;
          while ((m = regex.exec(child.text)) !== null) {
            codes.add(m[1]);
          }
        }
      });
    });

    // Fetch card data for the sidebar
    const fetchCards = async () => {
      const results = [];
      for (const code of codes) {
        try {
          const res = await fetch(
            `/api/cards/number/${encodeURIComponent(code)}?lang=EN`
          );
          if (res.ok) {
            const card = await res.json();
            results.push({ ...card, number: code });
          }
        } catch {
          // ignore
        }
      }
      setRelatedCards(results);
    };

    if (codes.size > 0) {
      setExpectedCardCount(codes.size);
      fetchCards();
    }
  }, [post]);

  // ── Helpers ──────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const estimateReadTime = () => {
    if (!post?.content) return 1;
    const text = post.content
      .map((b) => b.children?.map((c) => c.text || '').join(' '))
      .join(' ');
    return Math.max(1, Math.ceil(text.split(/\s+/).length / 200));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleTwitterShare = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post?.title || '');
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank'
    );
  };

  // ── Loading / Not found ──────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse space-y-4 max-w-3xl w-full px-4">
          <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
          <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
          <div className="h-64 bg-muted rounded" />
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-5/6" />
            <div className="h-4 bg-muted rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-lg text-muted-foreground">Article not found.</p>
        <Button variant="outline" onClick={() => navigate('/blog')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Button>
      </div>
    );
  }

  const coverUrl = post.coverImage
    ? urlFor(post.coverImage) || post.coverImage
    : null;

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>{post.title} | Gardeno Games Blog</title>
        {post.excerpt && (
          <meta name="description" content={post.excerpt} />
        )}
        <meta property="og:title" content={post.title} />
        {post.excerpt && (
          <meta property="og:description" content={post.excerpt} />
        )}
        {coverUrl && <meta property="og:image" content={coverUrl} />}
      </Helmet>

      <div className="min-h-[calc(100vh-48px)] bg-gradient-to-b from-background to-muted/30">
        {/* ── Hero header ─────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#3c618a] to-[#7a67ba] text-white">
          <div className="max-w-5xl mx-auto px-4 py-8">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-1 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-white/80">
                Home
              </Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/blog" className="hover:text-white/80">
                Blog
              </Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-white/90 truncate max-w-[200px]">
                {post.title}
              </span>
            </nav>

            <h1
              className="text-2xl md:text-4xl font-bold tracking-tight mb-3"
              style={{ fontFamily: 'Lato, Arial, sans-serif' }}
            >
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
              {post.author && (
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {post.author}
                </span>
              )}
              {post.publishedAt && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDate(post.publishedAt)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {estimateReadTime()} min read
              </span>
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {post.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="bg-white/15 text-white hover:bg-white/25 cursor-pointer"
                    onClick={() => navigate(`/blog?tag=${tag}`)}
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Body layout ─────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main content */}
            <article className="flex-1 min-w-0 max-w-3xl">
              {/* Cover image */}
              {coverUrl && (
                <img
                  src={coverUrl}
                  alt={post.title}
                  className="w-full rounded-lg shadow-md mb-8"
                />
              )}

              {/* Portable Text with auto card-code detection */}
              <div className="prose-custom">
                <BlogPortableText value={post.content} />
              </div>

              <Separator className="my-8" />

              {/* Share + Back */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/blog')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  All Articles
                </Button>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="h-4 w-4 mr-1" />
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTwitterShare}
                  >
                    <Twitter className="h-4 w-4 mr-1" />
                    Tweet
                  </Button>
                </div>
              </div>
            </article>

            {/* ── Sidebar: Related Cards ────────────────────── */}
            {expectedCardCount > 0 && (
              <aside className="w-full lg:w-72 flex-shrink-0">
                <div className="sticky top-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Cards Mentioned
                  </h3>
                  <ScrollArea className="max-h-[70vh]">
                    <div className="space-y-3 pr-2">
                      {relatedCards.map((card) => {
                        const imgUrl = getProxyImageUrl(
                          card.images?.blob?.full ||
                            card.images?.original?.full
                        );
                        return (
                          <Card
                            key={card.number}
                            className="overflow-hidden"
                          >
                            <CardContent className="p-2 flex gap-2">
                              {imgUrl && (
                                <img
                                  src={imgUrl}
                                  alt={card.name}
                                  className="w-14 rounded object-contain flex-shrink-0"
                                  style={{ height: '78px' }}
                                  loading="lazy"
                                />
                              )}
                              {!imgUrl && (
                                <div
                                  className="w-14 rounded bg-muted flex-shrink-0"
                                  style={{ height: '78px' }}
                                />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {card.name}
                                </p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {card.number}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {/* Skeleton placeholders for cards still loading */}
                      {relatedCards.length < expectedCardCount &&
                        Array.from({
                          length: expectedCardCount - relatedCards.length,
                        }).map((_, i) => (
                          <Card
                            key={`skeleton-${i}`}
                            className="overflow-hidden"
                          >
                            <CardContent className="p-2 flex gap-2 animate-pulse">
                              <div
                                className="w-14 rounded bg-muted flex-shrink-0"
                                style={{ height: '78px' }}
                              />
                              <div className="min-w-0 flex-1 space-y-2 py-1">
                                <div className="h-4 bg-muted rounded w-3/4" />
                                <div className="h-3 bg-muted rounded w-1/2" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
